import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, CloudRain, Flame, Music, X, CheckCircle2, Plus, Minus, BookOpen, Save, Volume2 } from 'lucide-react';
import { useBooks } from '../context/BookContext';

// Curated focus-music presets known to allow embedding.
const CURATED_STATIONS = [
  { name: 'Lofi Girl Beats ☕', yt: '5qap5aO4i9A', desc: 'Chill lofi study beats by Lofi Girl.' },
  { name: 'Cozy Bookstore 📚', yt: 'h2j3rV6xXhQ', desc: 'Rainy day window, fireplace, and quiet bookshop jazz.' },
  { name: 'Rainy Night Jazz 🌧️', yt: '5w3kLqJ_W8U', desc: 'Warm ambient coffee shop with gentle jazz melodies.' },
  { name: 'Cozy Forest Cabin 🪵', yt: 'L_LUpnjgPso', desc: 'Soft fireplace crackling and soothing piano notes.' }
];

const parseYouTubeEmbed = (raw) => {
  if (!raw) return null;
  const url = raw.trim();
  // playlist
  const list = url.match(/[?&]list=([A-Za-z0-9_-]+)/);
  if (list) return `https://www.youtube.com/embed/videoseries?list=${list[1]}`;
  // youtu.be short
  const short = url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  // standard watch URL
  const watch = url.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
  if (watch) return `https://www.youtube.com/embed/${watch[1]}`;
  // bare ID
  if (/^[A-Za-z0-9_-]{6,}$/.test(url)) return `https://www.youtube.com/embed/${url}`;
  return null;
};

const parseSpotifyEmbed = (raw) => {
  if (!raw) return null;
  const m = raw.trim().match(/open\.spotify\.com\/(track|playlist|album|episode|show)\/([A-Za-z0-9]+)/);
  if (!m) return null;
  return `https://open.spotify.com/embed/${m[1]}/${m[2]}`;
};

const playSoftChime = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    
    const playTone = (freq, delay, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + delay);
      
      gain.gain.setValueAtTime(0, ctx.currentTime + delay);
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + delay + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + duration);
      
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + duration);
    };
    
    // Cozy wind-chime arpeggio
    playTone(523.25, 0, 1.5);      // C5
    playTone(659.25, 0.12, 1.3);   // E5
    playTone(783.99, 0.24, 1.1);   // G5
    playTone(987.77, 0.36, 1.0);   // B5
  } catch (e) {
    console.error("Synthesizer chime failed:", e);
  }
};

const Focus = () => {
  const { books, addLog } = useBooks();
  const [isActive, setIsActive] = useState(false);
  const [isCountUp, setIsCountUp] = useState(false);
  const [initialSeconds, setInitialSeconds] = useState(1500);
  const [seconds, setSeconds] = useState(1500);
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState('');
  const [elapsedAtStop, setElapsedAtStop] = useState(0);
  const [ambientVolume, setAmbientVolume] = useState(0.5);

  // Focus Chime Settings
  const [chimeInterval, setChimeInterval] = useState('off');
  const lastChimeMinuteRef = useRef(0);

  // Background Tracking
  const endTimeRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Music picker: 'curated' | 'youtube' | 'spotify'
  const [musicSource, setMusicSource] = useState('curated');
  const [curatedIdx, setCuratedIdx] = useState(0);
  const [ytInput, setYtInput] = useState('');
  const [ytEmbed, setYtEmbed] = useState(null);
  const [spotifyInput, setSpotifyInput] = useState('');
  const [spotifyEmbed, setSpotifyEmbed] = useState(null);
  const [urlError, setUrlError] = useState('');

  const [sounds, setSounds] = useState({
    rain: { playing: false, url: '/audio/rain.mp3' },
    fire: { playing: false, url: '/audio/fire.mp3' }
  });

  const audioRefs = useRef({});

  // Wake Lock Logic (Keep screen on)
  const requestWakeLock = async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      } catch (err) {
        console.error(`${err.name}, ${err.message}`);
      }
    }
  };

  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  };

  // Timer Logic (Refactored for Background support)
  useEffect(() => {
    let interval = null;

    if (isActive) {
      requestWakeLock();
      // Set the exact time we should end
      if (!endTimeRef.current) {
        if (isCountUp) {
          endTimeRef.current = Date.now() - (seconds * 1000);
        } else {
          endTimeRef.current = Date.now() + (seconds * 1000);
        }
      }

      interval = setInterval(() => {
        const now = Date.now();
        if (isCountUp) {
          const elapsed = Math.round((now - endTimeRef.current) / 1000);
          setSeconds(elapsed);

          // Count Up Chime
          if (chimeInterval !== 'off') {
            const chimeSecs = parseInt(chimeInterval) * 60;
            const currentPeriod = Math.floor(elapsed / chimeSecs);
            if (currentPeriod > lastChimeMinuteRef.current && elapsed > 0) {
              lastChimeMinuteRef.current = currentPeriod;
              playSoftChime();
            }
          }
        } else {
          const remaining = Math.round((endTimeRef.current - now) / 1000);
          if (remaining <= 0) {
            handleAutoFinish();
            clearInterval(interval);
          } else {
            setSeconds(remaining);

            // Count Down Chime
            if (chimeInterval !== 'off') {
              const elapsed = initialSeconds - remaining;
              const chimeSecs = parseInt(chimeInterval) * 60;
              const currentPeriod = Math.floor(elapsed / chimeSecs);
              if (currentPeriod > lastChimeMinuteRef.current && elapsed > 0) {
                lastChimeMinuteRef.current = currentPeriod;
                playSoftChime();
              }
            }
          }
        }
      }, 500); // Check twice a second for precision
    } else {
      releaseWakeLock();
      endTimeRef.current = null;
      clearInterval(interval);
    }

    return () => {
      clearInterval(interval);
      releaseWakeLock();
    };
  }, [isActive, isCountUp, initialSeconds, chimeInterval]);

  // Visibility Change (Recalculate immediately when user returns to app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isActive && endTimeRef.current) {
        const now = Date.now();
        if (isCountUp) {
          const elapsed = Math.round((now - endTimeRef.current) / 1000);
          setSeconds(elapsed);
        } else {
          const remaining = Math.round((endTimeRef.current - now) / 1000);
          if (remaining <= 0) {
            handleAutoFinish();
          } else {
            setSeconds(remaining);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isActive, isCountUp]);

  useEffect(() => {
    Object.values(audioRefs.current).forEach(audio => {
      if (audio) audio.volume = ambientVolume;
    });
  }, [ambientVolume]);

  const handleAutoFinish = () => {
    setIsActive(false);
    setElapsedAtStop(initialSeconds);
    setShowLogModal(true);
    endTimeRef.current = null;
    lastChimeMinuteRef.current = 0;
  };

  const handleManualStop = () => {
    setIsActive(false);
    const elapsed = isCountUp ? seconds : (initialSeconds - seconds);
    if (elapsed >= 60) {
      setElapsedAtStop(elapsed);
      setShowLogModal(true);
    } else {
      setSeconds(isCountUp ? 0 : initialSeconds);
    }
    endTimeRef.current = null;
    lastChimeMinuteRef.current = 0;
  };

  const formatTime = (s) => {
    const mins = Math.floor(Math.max(0, s) / 60);
    const secs = Math.max(0, s) % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const adjustTimer = (amount) => {
    if (isActive) return;
    const newTotal = Math.max(300, initialSeconds + amount);
    setInitialSeconds(newTotal);
    setSeconds(newTotal);
  };

  const toggleSound = (key) => {
    setSounds(prev => ({
      ...prev,
      [key]: { ...prev[key], playing: !prev[key].playing }
    }));
  };

  const handleLogReading = async () => {
    const minutesRead = Math.floor(elapsedAtStop / 60);
    if (selectedBookId && minutesRead > 0) {
      const book = books.find(b => b.id === selectedBookId);
      await addLog({
        book_id: selectedBookId,
        title: book.title,
        date: new Date().toISOString().split('T')[0],
        minutes: minutesRead
      });
    }
    setShowLogModal(false);
    setSeconds(initialSeconds);
  };

  const selectedBook = books.find(b => b.id === selectedBookId);

  const handleLoadYouTube = () => {
    const embed = parseYouTubeEmbed(ytInput);
    if (!embed) { setUrlError('Paste a YouTube video or playlist URL.'); return; }
    setYtEmbed(embed);
    setUrlError('');
  };

  const handleLoadSpotify = () => {
    const embed = parseSpotifyEmbed(spotifyInput);
    if (!embed) { setUrlError('Paste a Spotify track, album, or playlist URL.'); return; }
    setSpotifyEmbed(embed);
    setUrlError('');
  };

  const curatedEmbed = `https://www.youtube.com/embed/${CURATED_STATIONS[curatedIdx].yt}?controls=1&modestbranding=1&rel=0&autoplay=0`;

  return (
    <div style={containerStyle}>
      {/* Book Selector */}
      <div style={bookPickerCard}>
        {!selectedBook ? (
          <div style={{ textAlign: 'center' }}>
            <BookOpen size={24} color="var(--text-soft)" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)', marginBottom: '8px', fontWeight: 'bold' }}>WHAT ARE WE READING?</div>
            <select 
              value={selectedBookId} 
              onChange={(e) => setSelectedBookId(e.target.value)}
              style={selectStyle}
            >
              <option value="">Choose a book to focus on...</option>
              {books.filter(b => b.status === 'reading').map(b => (
                <option key={b.id} value={b.id}>{b.title}</option>
              ))}
            </select>
          </div>
        ) : (
          <div style={selectedBookInfo}>
            <img src={selectedBook.cover} alt="" style={miniCover} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--accent)', fontWeight: 'bold' }}>WHAT ARE WE READING?</div>
              <div style={selectedTitle}>{selectedBook.title}</div>
              {!isActive && <button onClick={() => setSelectedBookId('')} style={changeBookBtn}>Switch Book</button>}
            </div>
          </div>
        )}
      </div>

      {/* Timer Section */}
      <div style={timerDisplay}>
        {!isActive && (
          <div style={modeTabContainer}>
            <button onClick={() => { setIsCountUp(false); setSeconds(1500); setInitialSeconds(1500); lastChimeMinuteRef.current = 0; }} style={!isCountUp ? activeModeTab : modeTab}>
              Countdown
            </button>
            <button onClick={() => { setIsCountUp(true); setSeconds(0); lastChimeMinuteRef.current = 0; }} style={isCountUp ? activeModeTab : modeTab}>
              Count-Up
            </button>
          </div>
        )}

        {/* Chime Interval Selector */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '-10px 0 15px 0', justifyContent: 'center' }}>
          <span style={{ fontSize: '0.62rem', color: 'var(--text-soft)', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Chime Reminder:
          </span>
          <select
            value={chimeInterval}
            disabled={isActive}
            onChange={(e) => {
              setChimeInterval(e.target.value);
              lastChimeMinuteRef.current = 0;
            }}
            style={selectDropdownStyle}
          >
            <option value="off">🔕 Off</option>
            <option value="1">⏰ Every 1 Min (Test)</option>
            <option value="5">🔔 Every 5 Mins</option>
            <option value="10">🔔 Every 10 Mins</option>
            <option value="15">🔔 Every 15 Mins</option>
            <option value="25">⏱️ Every 25 Mins (Pomodoro)</option>
          </select>
        </div>

        <div style={timerControls}>
          {!isCountUp && <button onClick={() => adjustTimer(-300)} disabled={isActive} style={adjustBtn}><Minus size={20} /></button>}
          <div style={{ ...timeText, animation: isActive ? 'pulse 2s infinite' : 'none' }}>{formatTime(seconds)}</div>
          {!isCountUp && <button onClick={() => adjustTimer(300)} disabled={isActive} style={adjustBtn}><Plus size={20} /></button>}
        </div>
        
        <div style={controls}>
          <button onClick={() => setIsActive(!isActive)} style={mainBtn}>
            {isActive ? <Pause size={32} /> : <Play size={32} />}
          </button>
          
          {isActive ? (
            <button onClick={handleManualStop} style={stopLogBtn}>
              <Save size={20} /> <span style={{ fontSize: '0.7rem' }}>STOP & LOG</span>
            </button>
          ) : (
            <button onClick={() => { setSeconds(isCountUp ? 0 : initialSeconds); lastChimeMinuteRef.current = 0; }} style={subBtn}>
              <RotateCcw size={24} />
            </button>
          )}
        </div>
      </div>

      {/* Sound Mixer */}
      <div style={mixerCard}>
        <div style={mixerGrid}>
          <SoundToggle active={sounds.rain.playing} onClick={() => toggleSound('rain')} icon={<CloudRain />} label="Rain" />
          <SoundToggle active={sounds.fire.playing} onClick={() => toggleSound('fire')} icon={<Flame />} label="Fire" />
        </div>

        <div style={volumeControl}>
          <Volume2 size={16} color="var(--text-soft)" />
          <input
            type="range" min="0" max="1" step="0.01"
            value={ambientVolume}
            onChange={(e) => setAmbientVolume(parseFloat(e.target.value))}
            style={sliderStyle}
          />
          <div style={{ fontSize: '0.6rem', color: 'var(--text-soft)', width: '30px' }}>{Math.round(ambientVolume * 100)}%</div>
        </div>

        {Object.keys(sounds).map(key => (
          sounds[key].playing && (
            <audio key={key} src={sounds[key].url} ref={el => audioRefs.current[key] = el} autoPlay loop />
          )
        ))}

        <div style={musicPlayer}>
          <div style={sourceTabs}>
            <button onClick={() => { setMusicSource('curated'); setUrlError(''); }} style={musicSource === 'curated' ? activeSourceBtn : sourceBtn}>
              <Music size={14} /> Curated
            </button>
            <button onClick={() => { setMusicSource('youtube'); setUrlError(''); }} style={musicSource === 'youtube' ? activeSourceBtn : sourceBtn}>
              YouTube
            </button>
            <button onClick={() => { setMusicSource('spotify'); setUrlError(''); }} style={musicSource === 'spotify' ? activeSourceBtn : sourceBtn}>
              Spotify
            </button>
          </div>

          {musicSource === 'curated' && (
            <>
              <div style={stationScrollContainer}>
                {CURATED_STATIONS.map((s, i) => (
                  <div 
                    key={i} 
                    onClick={() => setCuratedIdx(i)} 
                    style={curatedIdx === i ? activeStationCard : stationCard}
                  >
                    <div style={{ fontWeight: 'bold', fontSize: '0.75rem', marginBottom: '2px' }}>{s.name}</div>
                    <div style={{ fontSize: '0.55rem', opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.desc}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '12px' }}>
                <iframe
                  width="100%" height="160"
                  src={curatedEmbed}
                  title={CURATED_STATIONS[curatedIdx].name}
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  style={{ borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
                />
              </div>
            </>
          )}

          {musicSource === 'youtube' && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  placeholder="Paste a YouTube video or playlist URL"
                  value={ytInput}
                  onChange={(e) => setYtInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLoadYouTube()}
                  style={urlInputStyle}
                />
                <button onClick={handleLoadYouTube} style={loadBtnStyle}>Load</button>
              </div>
              {ytEmbed && (
                <iframe
                  width="100%" height="160" src={ytEmbed} title="YouTube"
                  frameBorder="0" allow="autoplay; encrypted-media"
                  style={{ borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', marginTop: '12px' }}
                />
              )}
            </div>
          )}

          {musicSource === 'spotify' && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  placeholder="Paste a Spotify track, album, or playlist URL"
                  value={spotifyInput}
                  onChange={(e) => setSpotifyInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLoadSpotify()}
                  style={urlInputStyle}
                />
                <button onClick={handleLoadSpotify} style={loadBtnStyle}>Load</button>
              </div>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-soft)', marginTop: '6px' }}>
                Sign in to Spotify in your browser for full-track playback.
              </div>
              {spotifyEmbed && (
                <iframe
                  width="100%" height="160" src={spotifyEmbed} title="Spotify"
                  frameBorder="0" allow="encrypted-media; autoplay; clipboard-write; fullscreen; picture-in-picture"
                  style={{ borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', marginTop: '12px' }}
                />
              )}
            </div>
          )}

          {urlError && (
            <div style={{ fontSize: '0.7rem', color: '#c0392b', marginTop: '8px' }}>{urlError}</div>
          )}
        </div>
      </div>

      {/* Completion Modal */}
      {showLogModal && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <CheckCircle2 size={48} color="var(--accent)" style={{ marginBottom: '1rem' }} />
            <h2>Session Summary</h2>
            <p>You spent <b>{Math.floor(elapsedAtStop / 60)} minutes</b> in the sanctuary.</p>
            {selectedBook ? (
              <p>Log this to <b>{selectedBook.title}</b>?</p>
            ) : (
              <p>Which book should we log this to?</p>
            )}
            
            <div style={{ display: 'flex', gap: '10px', width: '100%', marginTop: '24px' }}>
              <button onClick={handleLogReading} style={logBtn}>Log Progress</button>
              <button onClick={() => { setShowLogModal(false); setSeconds(initialSeconds); }} style={cancelBtn}>Discard</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SoundToggle = ({ active, onClick, icon, label }) => (
  <button onClick={onClick} style={active ? activeToggle : toggleStyle}>
    {React.cloneElement(icon, { size: 20 })}
    <span style={{ fontSize: '0.6rem', marginTop: '4px', fontWeight: 'bold' }}>{label}</span>
  </button>
);

// Styles
const containerStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0', gap: '1rem', minHeight: '80vh' };
const bookPickerCard = { width: '100%', maxWidth: '350px', backgroundColor: 'var(--bg-card)', padding: '15px', borderRadius: '20px', border: '1px solid var(--border)' };
const selectedBookInfo = { display: 'flex', gap: '15px', alignItems: 'center' };
const miniCover = { width: '45px', height: '65px', borderRadius: '6px', objectFit: 'cover', border: '1px solid var(--border)' };
const selectedTitle = { fontWeight: 'bold', fontSize: '0.9rem', color: 'var(--text-main)', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const changeBookBtn = { background: 'none', border: 'none', color: 'var(--accent)', fontSize: '0.7rem', padding: 0, textDecoration: 'underline', cursor: 'pointer' };
const timerDisplay = { textAlign: 'center' };
const timerControls = { display: 'flex', alignItems: 'center', gap: '10px' };
const adjustBtn = { background: 'none', border: 'none', color: 'var(--text-soft)', cursor: 'pointer', padding: '10px' };
const timeText = { fontSize: '5.5rem', fontWeight: '200', fontFamily: 'monospace', color: 'var(--text-main)', letterSpacing: '-3px' };
const controls = { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginTop: '5px' };
const mainBtn = { width: '70px', height: '70px', borderRadius: '50%', border: 'none', backgroundColor: 'var(--accent)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 8px 20px var(--accent-soft)' };
const subBtn = { background: 'none', border: 'none', color: 'var(--text-soft)', cursor: 'pointer' };
const stopLogBtn = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontWeight: 'bold' };
const mixerCard = { backgroundColor: 'var(--bg-card)', padding: '20px', borderRadius: '24px', width: '100%', maxWidth: '350px', border: '1px solid var(--border)' };
const mixerGrid = { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '15px' };
const volumeControl = { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '0 10px' };
const sliderStyle = { flex: 1, accentColor: 'var(--accent)', cursor: 'pointer' };
const toggleStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px 0', borderRadius: '16px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-app)', color: 'var(--text-soft)', cursor: 'pointer', transition: 'all 0.2s' };
const activeToggle = { ...toggleStyle, backgroundColor: 'var(--accent-soft)', color: 'var(--accent)', borderColor: 'var(--accent)' };
const musicPlayer = { borderTop: '1px solid var(--border)', paddingTop: '15px' };
const sourceTabs = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5px' };
const sourceBtn = { padding: '8px 4px', fontSize: '0.7rem', border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: 'var(--bg-app)', color: 'var(--text-soft)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontWeight: 'bold' };
const activeSourceBtn = { ...sourceBtn, backgroundColor: 'var(--accent-soft)', color: 'var(--accent)', borderColor: 'var(--accent)' };
const stationBtn = { padding: '6px 10px', fontSize: '0.7rem', border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: 'var(--bg-app)', color: 'var(--text-soft)', cursor: 'pointer' };
const activeStationBtn = { ...stationBtn, backgroundColor: 'var(--accent-soft)', color: 'var(--accent)', borderColor: 'var(--accent)', fontWeight: 'bold' };
const urlInputStyle = { flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-app)', color: 'var(--text-main)', fontSize: '0.8rem' };
const loadBtnStyle = { padding: '0 14px', borderRadius: '8px', border: 'none', backgroundColor: 'var(--accent)', color: 'white', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' };
const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' };
const modalContent = { backgroundColor: 'var(--bg-card)', padding: '35px 25px', borderRadius: '28px', textAlign: 'center', maxWidth: '400px', width: '100%' };
const selectStyle = { width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-app)', color: 'var(--text-main)', fontSize: '0.85rem' };
const logBtn = { flex: 2, padding: '16px', borderRadius: '14px', border: 'none', backgroundColor: 'var(--accent)', color: 'white', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem' };
const cancelBtn = { flex: 1, padding: '16px', borderRadius: '14px', border: '1px solid var(--border)', backgroundColor: 'transparent', color: 'var(--text-soft)', cursor: 'pointer', fontSize: '1rem' };

// Count-Up Mode Selector & Card Picker Styles
const modeTabContainer = {
  display: 'flex',
  backgroundColor: 'var(--bg-app)',
  borderRadius: '10px',
  border: '1px solid var(--border)',
  padding: '2px',
  marginBottom: '1rem',
  width: '180px',
  margin: '0 auto 1.5rem auto'
};

const modeTab = {
  flex: 1,
  padding: '6px',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  border: 'none',
  background: 'none',
  color: 'var(--text-soft)',
  cursor: 'pointer',
  transition: 'all 0.2s'
};

const activeModeTab = {
  ...modeTab,
  backgroundColor: 'var(--bg-card)',
  color: 'var(--accent)',
  boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
};

const stationScrollContainer = {
  display: 'flex',
  gap: '8px',
  overflowX: 'auto',
  padding: '5px 2px 10px 2px',
  marginTop: '10px',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none'
};

const stationCard = {
  flexShrink: 0,
  width: '110px',
  padding: '8px',
  borderRadius: '10px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)',
  color: 'var(--text-soft)',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'all 0.2s'
};

const activeStationCard = {
  ...stationCard,
  borderColor: 'var(--accent)',
  backgroundColor: 'var(--accent-soft)',
  color: 'var(--accent)',
  boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
};

const selectDropdownStyle = {
  padding: '6px 12px',
  borderRadius: '8px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)',
  color: 'var(--text-main)',
  fontSize: '0.72rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  outline: 'none',
  textAlign: 'center'
};

export default Focus;
